import { useState } from "react";
import { Archive, Download, ShieldCheck, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../../lib/api";
import CriticalPinDialog from "../CriticalPinDialog";

export default function BackupTab() {
    const [restoreFile, setRestoreFile] = useState(null);
    const [pinOpen, setPinOpen] = useState(false);
    const [result, setResult] = useState(null);

    const restore = async (pin) => {
        try {
            const fd = new FormData();
            fd.append("file", restoreFile);
            const r = await api.post("/admin/backup/restore", fd, { headers: { "X-Critical-Pin": pin, "Content-Type": "multipart/form-data" } });
            setResult(r.data);
            setPinOpen(false);
            setRestoreFile(null);
            toast.success(`${r.data.restored} kayıt geri yüklendi (${r.data.skipped} mevcut kayıt atlandı)`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Geri yükleme başarısız");
        }
    };

    return (
        <div className="space-y-4" data-testid="admin-backup-tab">
            <div className="flex flex-wrap items-center gap-3 rounded-2xl border bg-card p-5">
                <Archive className="h-6 w-6 text-terra-600" />
                <div className="text-sm">
                    <p className="font-semibold">Tam Platform Yedeği</p>
                    <p className="text-xs text-muted-foreground">
                        Tüm platform verileri (kullanıcılar, firmalar, tedarikçiler, kategoriler, ürünler, siparişler, faturalar, mesajlar, içerikler, ayarlar ve daha fazlası) tek JSON dosyası olarak indirilir. Şifre hash'leri ve API gizli anahtarları güvenlik gereği dışa aktarılmaz.
                    </p>
                </div>
                <a
                    href={`${API}/backup`}
                    className="ml-auto inline-flex h-9 items-center gap-2 rounded-lg bg-terra-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-terra-700"
                    data-testid="backup-download-button"
                >
                    <Download className="h-4 w-4" /> Yedekle (İndir)
                </a>
            </div>

            <div className="flex flex-wrap items-center gap-3 rounded-2xl border bg-card p-5">
                <Upload className="h-6 w-6 text-terra-600" />
                <div className="text-sm">
                    <p className="font-semibold">Yedeği Geri Yükle</p>
                    <p className="text-xs text-muted-foreground">
                        Daha önce indirilen yedek dosyasını (.json) seçin. Yalnızca sistemde <span className="font-semibold">olmayan</span> kayıtlar eklenir — mevcut kayıtlar ezilmez. Silinmiş verileri kurtarmak için uygundur.
                    </p>
                </div>
                <label className="ml-auto inline-flex h-9 cursor-pointer items-center gap-2 rounded-lg border border-terra-300 px-4 text-sm font-semibold text-terra-700 transition-colors hover:bg-terra-50" data-testid="backup-restore-button">
                    <input
                        type="file"
                        accept=".json"
                        className="hidden"
                        onChange={(e) => { if (e.target.files[0]) { setRestoreFile(e.target.files[0]); setPinOpen(true); } e.target.value = ""; }}
                        data-testid="backup-restore-input"
                    />
                    <Upload className="h-4 w-4" /> Yedeği Geri Yükle
                </label>
            </div>

            {result && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4" data-testid="backup-restore-result">
                    <p className="text-sm font-semibold text-emerald-800">Geri yükleme tamamlandı: {result.restored} kayıt eklendi, {result.skipped} atlandı</p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                        {Object.entries(result.details || {}).map(([col, d]) => (
                            <span key={col} className="rounded-full border border-emerald-300 bg-white px-2.5 py-0.5 text-[11px] font-medium text-emerald-800" data-testid={`restore-detail-${col}`}>
                                {col}: +{d.restored}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex flex-wrap items-center gap-3 rounded-2xl border bg-card p-5">
                <Archive className="h-6 w-6 text-emerald-600" />
                <div className="text-sm">
                    <p className="font-semibold">Otomatik Haftalık Yedek</p>
                    <p className="text-xs text-muted-foreground">
                        Her Pazartesi 09:00'da (TSİ) tam platform yedeği otomatik alınır, son 4 yedek saklanır ve e-posta ile özet gönderilir. En son otomatik yedeği buradan indirebilirsiniz.
                    </p>
                </div>
                <a
                    href={`${API}/backup/auto-latest`}
                    className="ml-auto inline-flex h-9 items-center gap-2 rounded-lg border border-emerald-300 px-4 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-50"
                    data-testid="backup-auto-latest-button"
                >
                    <Download className="h-4 w-4" /> Son Otomatik Yedeği İndir
                </a>
            </div>

            <div className="flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
                <p data-testid="backup-info">Her indirme ve geri yükleme denetim kaydına işlenir (DataBackup / BackupRestore / AutoBackup). Dosya adı indirme tarihi ve saatini içerir; düzenli aralıklarla indirip güvenli bir yerde saklamanız önerilir.</p>
            </div>

            <CriticalPinDialog
                open={pinOpen}
                onClose={() => { setPinOpen(false); setRestoreFile(null); }}
                title="Yedeği Geri Yükle"
                message={`"${restoreFile?.name || ""}" dosyasındaki kayıtlar geri yüklenecek. Yalnızca sistemde olmayan kayıtlar eklenir; mevcut veriler EZİLMEZ. Devam etmek için kritik işlem şifresini girin.`}
                onSubmit={restore}
            />
        </div>
    );
}
